import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Download, FileText, Link2, Loader2, LogOut, MailCheck, RefreshCw, Unlink, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/components/AuthContext";
import { ChecklistAssistant } from "@/components/ChecklistAssistant";
import { ConsultationAiPanel } from "@/components/ConsultationAiPanel";
import { SERVICE_LABEL_LOOKUP } from "@/data/serviceOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusLabels = { scheduled: "Scheduled", confirmed: "Confirmed", completed: "Completed", cancelled: "Cancelled" };

const formatAppointment = (value) => new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
}).format(new Date(value));

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/overview`);
      setOverview(response.data);
    } catch (error) {
      if (error.response?.status === 401) navigate("/staff/login");
      else toast.error("Dashboard data could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (consultationId, status) => {
    try {
      await axios.patch(`${API}/staff/consultations/${consultationId}`, { status });
      toast.success(`Appointment marked ${statusLabels[status]}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Status could not be updated");
    }
  };

  const sendReminder = async (consultationId) => {
    try {
      await axios.post(`${API}/staff/consultations/${consultationId}/send-reminder`);
      toast.success("Client reminder email sent");
      load();
    } catch (error) {
      toast.error("Reminder could not be sent");
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/staff/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = doc.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Document could not be downloaded");
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/staff/login");
  };

  const connectCalendar = async () => {
    try {
      const response = await axios.get(`${API}/staff/calendar/connect`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google Calendar is not configured");
    }
  };

  const disconnectCalendar = async () => {
    try {
      await axios.delete(`${API}/staff/calendar/disconnect`);
      toast.success("Google Calendar disconnected");
      load();
    } catch (error) {
      toast.error("Google Calendar could not be disconnected");
    }
  };

  const stats = overview?.stats || { consultations: 0, documents: 0, upcoming: 0 };

  return (
    <main className="min-h-screen bg-brand-sand text-brand-navy" data-testid="staff-dashboard-page">
      <header className="border-b border-brand-navy/10 bg-white/80 backdrop-blur-xl">
        <div className="section-shell flex min-h-20 flex-wrap items-center justify-between gap-4 py-4">
          <div>
            <p className="overline text-brand-copper">Protected Staff Area</p>
            <h1 className="font-serif text-3xl font-bold">Intake Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-bold text-brand-navy/60 sm:block" data-testid="staff-user-email">{user?.email}</span>
            <Button variant="outline" onClick={load} data-testid="staff-refresh-button" className="rounded-full">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
            <Button onClick={signOut} data-testid="staff-logout-button" className="rounded-full bg-brand-navy text-white">
              <LogOut className="h-4 w-4" /> Sign Out
            </Button>
          </div>
        </div>
      </header>
      <div className="section-shell py-8 lg:py-12">
        <div className="grid gap-4 md:grid-cols-4">
          {[
            { label: "Consultations", value: stats.consultations, icon: Users, testId: "staff-stat-consultations" },
            { label: "Upcoming", value: stats.upcoming, icon: CalendarDays, testId: "staff-stat-upcoming" },
            { label: "Documents", value: stats.documents, icon: FileText, testId: "staff-stat-documents" },
            { label: "Reminders", value: "Email", icon: MailCheck, testId: "staff-stat-reminders" },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="rounded-lg bg-white p-6 shadow-sm" data-testid={item.testId}>
                <Icon className="h-6 w-6 text-brand-copper" />
                <p className="mt-5 font-serif text-3xl font-black">{item.value}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-brand-navy/50">{item.label}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex flex-col gap-4 rounded-lg border border-brand-navy/10 bg-brand-cream p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="staff-calendar-status">
          <div>
            <p className="text-sm font-extrabold">Google Calendar Sync</p>
            <p className="mt-1 text-sm text-brand-navy/62">
              {overview?.calendar?.connected
                ? `Connected as ${overview.calendar.google_email}. New and rescheduled appointments sync automatically.`
                : overview?.calendar?.configured
                  ? "Google credentials detected. Connect the office calendar to enable automatic events."
                  : "Awaiting GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET. Appointments remain saved internally."}
            </p>
          </div>
          {overview?.calendar?.connected ? (
            <Button variant="outline" onClick={disconnectCalendar} data-testid="calendar-disconnect-button" className="rounded-full">
              <Unlink className="h-4 w-4" /> Disconnect
            </Button>
          ) : (
            <Button onClick={connectCalendar} disabled={!overview?.calendar?.configured} data-testid="calendar-connect-button" className="rounded-full bg-brand-navy text-white">
              <Link2 className="h-4 w-4" /> Connect Calendar
            </Button>
          )}
        </div>
        <ChecklistAssistant />
        {overview?.document_intakes?.length > 0 && (
          <section className="mt-8 rounded-lg bg-white p-6 shadow-sm" data-testid="staff-document-intakes-section">
            <p className="overline text-brand-copper">Document Uploads</p>
            <h2 className="mt-2 font-serif text-2xl font-bold">Unscheduled document submissions</h2>
            <div className="mt-6 space-y-4">
              {overview.document_intakes.map((session) => (
                <article key={session.id} className="rounded-lg border border-brand-navy/10 p-5" data-testid={`staff-document-session-${session.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-extrabold">Session #{session.id.slice(0, 8)}</p>
                      <p className="mt-1 text-xs font-semibold text-brand-navy/52">Uploaded {new Date(session.created_at).toLocaleString("en-US", { timeZone: "America/Los_Angeles" })} PT</p>
                    </div>
                    <span className="rounded-full bg-brand-cream px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em]">{session.documents.length} file{session.documents.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {session.documents.map((document) => (
                      <Button key={document.id} variant="outline" onClick={() => downloadDocument(document)} data-testid={`download-unscheduled-document-${document.id}`} className="rounded-full">
                        <Download className="h-4 w-4" /> {document.filename}
                      </Button>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
        {loading ? (
          <div className="grid min-h-64 place-items-center" data-testid="staff-dashboard-loading"><Loader2 className="h-8 w-8 animate-spin text-brand-copper" /></div>
        ) : (
          <div className="mt-8 space-y-5" data-testid="staff-consultation-list">
            {overview?.consultations?.length ? overview.consultations.map((item) => (
              <article key={item.id} className="rounded-lg bg-white p-6 shadow-sm" data-testid={`staff-consultation-${item.id}`}>
                <div className="grid gap-6 lg:grid-cols-[1fr_220px]">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="font-serif text-2xl font-bold">{item.name}</h2>
                      <span className="rounded-full bg-brand-cream px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em]">{statusLabels[item.status || "scheduled"]}</span>
                    </div>
                    <p className="mt-2 text-sm font-bold text-brand-navy/70">{formatAppointment(item.appointment_start)} PT</p>
                    <p className="mt-2 break-all text-sm text-brand-navy/62">{item.email} · {item.phone}</p>
                    <p className="mt-4 text-sm leading-7 text-brand-navy/70"><strong>Service:</strong> {SERVICE_LABEL_LOOKUP[item.service] || item.service} · <strong>Stage:</strong> {item.stage}</p>
                    <p className="mt-3 rounded-lg bg-brand-sand p-4 text-sm leading-7">{item.message}</p>
                    <ConsultationAiPanel consultation={item} onSaved={load} />
                  </div>
                  <div className="space-y-3">
                    <Select value={item.status || "scheduled"} onValueChange={(status) => updateStatus(item.id, status)}>
                      <SelectTrigger data-testid={`appointment-status-select-${item.id}`} className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(statusLabels).map(([value, label]) => <SelectItem key={value} value={value} data-testid={`status-option-${item.id}-${value}`}>{label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => sendReminder(item.id)} data-testid={`send-reminder-${item.id}`} className="h-12 w-full rounded-lg">
                      <MailCheck className="h-4 w-4" /> Send Reminder
                    </Button>
                  </div>
                </div>
                <div className="mt-6 border-t border-brand-navy/10 pt-5">
                  <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.18em] text-brand-navy/48">Encrypted Documents</p>
                  {item.documents.length ? (
                    <div className="flex flex-wrap gap-3">
                      {item.documents.map((document) => (
                        <Button key={document.id} variant="outline" onClick={() => downloadDocument(document)} data-testid={`download-document-${document.id}`} className="rounded-full">
                          <Download className="h-4 w-4" /> {document.filename}
                        </Button>
                      ))}
                    </div>
                  ) : <p className="text-sm text-brand-navy/50">No documents uploaded yet.</p>}
                </div>
              </article>
            )) : (
              <div className="rounded-lg bg-white p-12 text-center" data-testid="staff-empty-state">
                <p className="font-serif text-2xl font-bold">No consultation requests yet.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
